// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Delivery addresses  (route /account/addresses — the saved-address book)
// Lists the signed-in customer's addresses (default first) and lets them add, edit, set a default
// and delete. Add/edit reuse components/delivery/AddressForm. Deleting asks for confirmation; if the
// default is deleted the server promotes another one. Signed-out visitors are sent to /welcome.
// API: GET/POST/PUT/DELETE /api/me/addresses (utils/addresses.ts).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Briefcase, CircleAlert, Home, LocateFixed, Loader2, MapPin, Pencil, Plus, ShieldCheck, Star, Trash2, Users } from "lucide-react";
import PageTransition from "../components/PageTransition";
import AddressForm from "../components/delivery/AddressForm";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { fetchProfile } from "../utils/profile";
import { MAX_ADDRESSES, deleteAddress, fetchAddresses, setDefaultAddress, type Address, type AddressLabel } from "../utils/addresses";

const LABELS: Record<AddressLabel, { text: string; icon: typeof Home }> = {
  home: { text: "Home", icon: Home },
  work: { text: "Work", icon: Briefcase },
  other: { text: "Friends & family", icon: Users },
};

type View = { kind: "list" } | { kind: "form"; editing: Address | null; locate?: boolean };
type Load = { status: "loading" } | { status: "error" } | { status: "ready"; addresses: Address[] };

export default function AddressBook() {
  useDocumentTitle("Delivery addresses");
  const auth = useAuth();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [view, setView] = useState<View>({ kind: "list" });
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Who the parcel is for: the account holder's name and number (an address doesn't store its own).
  const [contact, setContact] = useState<{ name: string | null; phone: string | null } | null>(null);
  const signedIn = auth.status === "signed-in";

  const refresh = useCallback(async () => {
    try {
      setLoad({ status: "ready", addresses: await fetchAddresses() });
    } catch {
      setLoad({ status: "error" });
    }
  }, []);

  useEffect(() => {
    if (signedIn) void refresh();
    if (signedIn) fetchProfile().then((p) => setContact({ name: p.fullName, phone: p.phone }), () => undefined);
  }, [signedIn, refresh]);

  // Move focus to the heading when the view changes, so keyboard and screen-reader users land in it.
  useEffect(() => heading.current?.focus(), [view]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const remove = async (address: Address) => {
    setBusyId(address.id);
    setError(null);
    try {
      await deleteAddress(address.id);
      setConfirmingId(null);
      await refresh(); // show the new list first, then announce, so the message matches what's on screen
      setNotice("Address deleted.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't delete that address. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const makeDefault = async (address: Address) => {
    setBusyId(address.id);
    setError(null);
    try {
      await setDefaultAddress(address.id);
      await refresh();
      setNotice("Default address updated.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't update your default address.");
    } finally {
      setBusyId(null);
    }
  };

  const addresses = load.status === "ready" ? load.addresses : [];
  const atLimit = addresses.length >= MAX_ADDRESSES;

  if (view.kind === "form") {
    return (
      <PageTransition>
        <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
          <header className="sticky top-8 z-40 bg-foam-50/90 shadow-[0_1px_8px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:bg-ocean-950/90">
            <div className="mx-auto flex h-16 max-w-xl items-center gap-2 px-4">
              <button
                type="button"
                aria-label="Back to saved addresses"
                onClick={() => setView({ kind: "list" })}
                className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-teal-600"
              >
                <ArrowLeft className="h-6 w-6" aria-hidden="true" />
              </button>
              <h1 ref={heading} tabIndex={-1} className="font-display text-lg font-semibold outline-none">
                {view.editing ? "Edit address" : "Add a new address"}
              </h1>
            </div>
          </header>
          <AddressForm
            initial={view.editing}
            autoLocate={view.locate === true}
            submitLabel={view.editing ? "Save changes" : "Save address"}
            onSaved={() => {
              const message = view.editing ? "Address updated." : "Address saved.";
              setView({ kind: "list" });
              void refresh().then(() => setNotice(message));
            }}
          />
        </div>
      </PageTransition>
    );
  }

  const openForm = (editing: Address | null, locate = false) => {
    setNotice(null);
    setView({ kind: "form", editing, locate });
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <header className="mx-auto flex max-w-xl items-center gap-2 px-4 pt-4">
          <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:ring-white/10">
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 ref={heading} tabIndex={-1} className="font-display text-xl font-bold outline-none">My addresses</h1>
            <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">
              {load.status === "ready" ? `${addresses.length} saved ${addresses.length === 1 ? "address" : "addresses"}` : "Where we deliver"}
            </p>
          </div>
          {load.status === "ready" && (
            <button
              type="button"
              disabled={atLimit}
              onClick={() => openForm(null)}
              className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white shadow-md hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> Add new<span className="sr-only"> address</span>
            </button>
          )}
        </header>

        <main className="mx-auto flex max-w-xl flex-col gap-4 px-4 pb-16 pt-4">
          <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
          <p role="alert" className="flex items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">
            {error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
            {error}
          </p>

          <Link to="/coming-soon/coverage-radar" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>Delivery coverage &amp; hubs</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          {load.status === "loading" && (
            <p role="status" className="mt-8 flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              Loading your addresses…
            </p>
          )}

          {load.status === "error" && (
            <div role="alert" className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-semibold">We couldn&apos;t load your addresses.</p>
              <button type="button" onClick={() => void refresh()} className="mt-3 min-h-11 rounded-full bg-ocean-800 px-6 font-bold text-white">
                Try again
              </button>
            </div>
          )}

          <ul className="flex flex-col gap-4">
            {addresses.map((address) => {
              const { text, icon: Icon } = LABELS[address.label];
              const confirming = confirmingId === address.id;
              const busy = busyId === address.id;
              const requests = [
                address.instructions.leaveWithSecurity && "Leave with security",
                address.instructions.directHandover && "Hand over directly",
                address.instructions.whatsappBeforeArrival && "WhatsApp before arrival",
              ].filter(Boolean);
              return (
                <li key={address.id} className="relative overflow-hidden rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" data-testid="address-card">
                  {address.isDefault && <span aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-ocean-700 to-teal-600" />}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-ocean-700/10 px-2.5 py-1 text-[11px] font-bold uppercase text-ocean-900 dark:bg-white/10 dark:text-foam-50">
                        <Icon className="h-3.5 w-3.5" aria-hidden="true" /> {text}
                      </span>
                      {address.isDefault && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-1 text-[11px] font-bold uppercase text-teal-700">
                          <Star className="h-3 w-3" aria-hidden="true" /> Default
                        </span>
                      )}
                    </div>
                    {!confirming && (
                      <div className="flex shrink-0 items-center gap-1">
                        {!address.isDefault && (
                          <button type="button" onClick={() => void makeDefault(address)} disabled={busy} className="inline-flex min-h-11 items-center gap-1 rounded-full bg-ocean-700/10 px-3 text-xs font-bold text-ocean-900 hover:bg-ocean-700/15 disabled:opacity-60 dark:bg-white/10 dark:text-foam-50">
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Set as default<span className="sr-only"> {text} address</span>
                          </button>
                        )}
                        <button type="button" onClick={() => openForm(address)} aria-label={`Edit ${text} address`} className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-900 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-foam-50">
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => { setNotice(null); setConfirmingId(address.id); }} aria-label={`Delete ${text} address`} className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-500/10 text-coral-600 hover:bg-coral-500/20 focus-visible:outline-2 focus-visible:outline-teal-600 dark:text-coral-300">
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>

                  {contact && (contact.name || contact.phone) && (
                    <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-3">
                      <p className="font-display text-lg font-bold">{contact.name ?? "Your name"}</p>
                      {contact.phone && <p className="text-sm font-semibold text-ocean-800/85 dark:text-foam-100/80">{contact.phone}</p>}
                    </div>
                  )}
                  <p className="mt-1 text-sm leading-relaxed text-ocean-800/90 dark:text-foam-100/85">
                    {address.house}, {address.street}
                    {address.landmark ? `, near ${address.landmark}` : ""}, {address.city}, {address.state} - {address.pincode}
                  </p>

                  {requests.length > 0 && (
                    <div className="mt-3 flex items-start gap-2 rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
                      <ShieldCheck className="mt-0.5 h-[18px] w-[18px] shrink-0 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:text-foam-100">Delivery requests</p>
                        <p className="text-xs text-ocean-800/90 dark:text-foam-100/85">{requests.join(" · ")}</p>
                      </div>
                    </div>
                  )}

                  {confirming && (
                    <div role="alertdialog" aria-label={`Delete ${text} address?`} className="mt-3 rounded-xl bg-coral-500/10 p-3">
                      <p className="text-sm font-semibold">Delete this address?{address.isDefault && addresses.length > 1 ? " Another address will become your default." : ""}</p>
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => void remove(address)} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-60">
                          {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                          Yes, delete
                        </button>
                        <button type="button" onClick={() => setConfirmingId(null)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold">
                          Keep it
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {load.status === "ready" && (
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-ocean-700/5 p-6 text-center dark:bg-white/5">
              <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-ocean-800 shadow-sm dark:bg-ocean-900 dark:text-foam-100">
                <MapPin className="h-6 w-6" />
              </span>
              <p className="font-display text-lg font-bold text-ocean-900 dark:text-foam-50">{addresses.length === 0 ? "No saved addresses yet" : "Add a new delivery address"}</p>
              <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Pin it on the map or enter the house details.</p>
              <div className="mt-1 flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  disabled={atLimit}
                  onClick={() => openForm(null, true)}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-bold text-ocean-900 shadow-sm hover:bg-ocean-700/5 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-ocean-900 dark:text-foam-50"
                >
                  <LocateFixed className="h-4 w-4" aria-hidden="true" /> Use current live location
                </button>
                <button
                  type="button"
                  disabled={atLimit}
                  onClick={() => openForm(null)}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-800 px-5 text-sm font-bold text-white shadow-md hover:bg-ocean-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" /> Add a new address
                </button>
              </div>
              {atLimit && <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">You&apos;ve saved the maximum of {MAX_ADDRESSES} addresses. Delete one to add another.</p>}
            </div>
          )}
        </main>
      </div>
    </PageTransition>
  );
}
